import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { DataTable } from 'components/table/DataTable';
import { useModelService } from 'services/model/model.service';
import { createArticleImageCell } from 'components/table/DataTableUtil';
import { FormattedMessage, useIntl } from 'react-intl';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { useEntriesDemandReportData } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/queryHooks';
import { useContext, useEffect, useState } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import {
  buildStandardColumns,
  createCellExportOptions,
  DemandTableProps,
  DemandTableRow,
  digestData,
} from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandTableHelpers';
import { ProductDetailsTableRow } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/ProductDetailsTableRow';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEMAND_MONITORING_ROOT } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandHome';

interface DemandProductTableRow extends DemandTableRow {
  prdNumber: string;
  prdName: string;
  artImageUrl: string;
}

export function ProductTable({
  merchantData,
  filters,
  localFilterFunction,
  disableClick = false,
}: DemandTableProps & { disableClick?: boolean }) {
  const intl = useIntl();
  const tenantContext = useContext(TenantConfigContext);
  const columnHelper = createColumnHelper<DemandProductTableRow>();
  const navigate = useNavigate();
  const location = useLocation();

  const { getMeasureByMetric, getAttributes } = useModelService();

  const [prdNumber, prdName, artImageUrl] = getAttributes(['prdNumber', 'prdName', 'artImageUrl']);
  const ordGmvVal = getMeasureByMetric('ordGmvVal', 'OD');
  const ordQty = getMeasureByMetric('ordQty', 'OD');

  const buildColumns = () => {
    const defaultColumns = buildStandardColumns(ordQty, intl) as ColumnDef<ProductDetailsTableRow>[];

    return (
      [
        columnHelper.accessor('artImageUrl', {
          enableSorting: false,
          // eslint-disable-next-line react/no-unstable-nested-components
          header: () => <FormattedMessage id="general.label.image" />,
          cell: info => createArticleImageCell(info, tenantContext),
        }),

        columnHelper.accessor('prdNumber', {
          id: 'prdNumber',
          header: () => prdNumber.name,
          cell: info => info.getValue(),
        }),

        columnHelper.accessor('prdName', {
          id: 'prdName',
          header: () => prdName.name,
          cell: info => info.getValue(),
        }),
      ] as ColumnDef<ProductDetailsTableRow>[]
    ).concat(defaultColumns) as ColumnDef<DemandProductTableRow>[];
  };

  const cols: ColumnDef<DemandProductTableRow>[] = buildColumns();

  const { data } = useEntriesDemandReportData(prdNumber.code, filters);

  const { currentDataVersion } = useContext(TenantConfigContext);

  const [tableRows, setTableRows] = useState<DemandProductTableRow[]>([]);

  useEffect(() => {
    if (currentDataVersion) {
      digestData(
        merchantData,
        data,
        prdNumber,
        ordGmvVal,
        ordQty,
        entry => ({
          prdNumber: prdNumber.getValue(entry.attributes),
          prdName: prdName.getValue(entry.attributes),
          artImageUrl: artImageUrl.getValue(entry.attributes),
        }),
        setTableRows,
        localFilterFunction
      );
    }
  }, [data, merchantData, currentDataVersion, localFilterFunction]);

  const showDetails = (row: Row<DemandProductTableRow>) => {
    navigate(`${DEMAND_MONITORING_ROOT}/detail/${prdNumber.code}/${row.original.prdNumber}${location.search}`);
  };

  return (
    <>
      <DataTableContextProvider>
        <DataTable
          onRowClick={disableClick ? undefined : showDetails}
          rows={tableRows}
          columns={cols}
          titleId="insights.product"
          defaultSortingState={[{ id: 'ordGmvVal', desc: true }]}
          exportConfigOptions={createCellExportOptions('Products')}
        />
      </DataTableContextProvider>
    </>
  );
}
